/**
 * Hero de /tribunal : une salle d'audience tirée au hasard à chaque visite
 * (la cour française ou l'une de ses quatre déclinaisons, en calques animés),
 * un bandeau « qui parle » qui enchaîne trois répliques, et les audiences en
 * direct.
 */
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { getCurrentLanguage } from '@/i18n';

import { COURTS, CourtDiorama, portraitUrl, type CourtKey, type StageRole } from './Courtroom';
import { readLocal, writeLocal } from './trialTypes';

/** Qui dit chacune des trois répliques de la salle (cf. tribunal.home.courts.*.lines). */
const SPEAKERS: Record<CourtKey, StageRole[]> = {
  fr: ['judge', 'witness', 'defense'],
  us: ['defense', 'judge', 'witness'],
  uk: ['defense', 'judge', 'prosecution'],
  gr: ['prosecution', 'defense', 'judge'],
  eu: ['prosecution', 'defense', 'judge'],
};
const BEAT_MS = 4200;

interface LiveItem { id: string; title: string; current_phase: string }

export function CourtHero({ onSeat, onOpen }: { onSeat: () => void; onOpen: () => void }) {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  // Une salle au hasard à chaque visite (jamais deux fois de suite la même)
  const [court, setCourt] = useState<CourtKey>(() => {
    const last = readLocal('tribunal_court');
    const pool = COURTS.filter(c => c !== last);
    return pool[Math.floor(Math.random() * pool.length)];
  });
  useEffect(() => { writeLocal('tribunal_court', court); }, [court]);
  const next = () => setCourt(c => COURTS[(COURTS.indexOf(c) + 1) % COURTS.length]);

  const [beat, setBeat] = useState(0);
  const [loop, setLoop] = useState(0);
  useEffect(() => { setBeat(0); }, [court]);
  useEffect(() => {
    if (reduce) return;
    const id = setTimeout(() => {
      setBeat(b => (b + 1) % 3);
      if (beat === 2) setLoop(l => l + 1);
    }, BEAT_MS);
    return () => clearTimeout(id);
  }, [beat, reduce]);

  const [live, setLive] = useState<LiveItem[]>([]);
  useEffect(() => {
    fetch(`/api/trials?status=in_progress&limit=3&offset=0&lang=${getCurrentLanguage()}`)
      .then(r => (r.ok ? r.json() : { trials: [] }))
      .then(d => setLive(d.trials ?? []))
      .catch(() => setLive([]));
  }, []);

  const lines = t(`tribunal.home.courts.${court}.lines`, { returnObjects: true }) as { who: string; text: string }[];
  const line = Array.isArray(lines) ? lines[beat] : null;
  const name = t(`tribunal.home.courts.${court}.name`);
  const place = t(`tribunal.home.courts.${court}.place`);
  const speaker = SPEAKERS[court][beat];

  const scene = (mobile: boolean) => (
    <CourtDiorama
      court={court}
      fullStage
      talking={reduce ? null : speaker}
      spotlight={speaker}
      judgeStrikeKey={`hero-${loop}`}
      span={mobile ? 1000 : 1440}
      // Grand écran : le juge à droite du titre, comme dans la cour française
      anchor={mobile || court === 'fr' ? undefined : 0.63}
      parallax={!mobile}
    />
  );

  const face = (size: number) => (
    <img src={portraitUrl(speaker, '', court)} alt="" className="shrink-0 rounded-full bg-[#efe3c4] ring-2 ring-[#a8742a]" style={{ width: size, height: size }} />
  );

  const lowerThird = (size: number) => (
    <AnimatePresence mode="wait" initial={false}>
      {line && (
        <motion.div
          key={`${court}-${beat}`}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.3 }}
          className="flex items-center gap-3 min-w-0"
          aria-live="polite"
        >
          {face(size)}
          <div className="min-w-0">
            <div className="text-[11px] font-bold tracking-wide text-[#a8742a]">{line.who}</div>
            <div className="font-serif text-[15px] sm:text-base leading-snug text-[#2a1d12]">{line.text}</div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const courtChip = (
    <div className="flex items-center gap-2.5 pl-3.5 pr-1.5 py-1.5 rounded-full bg-[#f6efe0]/95 shadow-[0_6px_20px_rgba(42,29,18,.25)] text-[13px] text-[#2a1d12]">
      <span className="sr-only">{t('tribunal.home.courts_label')} : </span>
      <span className="font-['IM_Fell_English',Georgia,serif] text-[15px] leading-none">{name}</span>
      <span className="hidden sm:inline italic text-[#6e5f4b]">{place}</span>
      <button
        onClick={next}
        className="h-8 px-3 rounded-full border border-[#d9ccb2] bg-[#fffdf8] hover:bg-white text-xs font-bold text-[#2a1d12]"
      >
        {t('tribunal.home.other_court')} ↻
      </button>
    </div>
  );

  const ctas = (
    <div className="flex flex-col sm:flex-row gap-2.5">
      <button onClick={onSeat} className="h-12 px-6 rounded-xl bg-[#2a1d12] hover:bg-[#4a3a28] text-[#f6efe0] font-bold">
        {t('tribunal.home.cta_seat')}
      </button>
      <button onClick={onOpen} className="h-12 px-6 rounded-xl bg-white hover:bg-[#fffdf8] border border-[#d6c8ad] text-[#2a1d12] font-bold">
        {t('tribunal.home.cta_open')}
      </button>
    </div>
  );

  const intro = (
    <>
      <div className="text-[11px] sm:text-xs font-bold uppercase tracking-[.14em] text-[#7a6650]">{t('tribunal.home.presents')}</div>
      <h1 className="mt-2 font-['IM_Fell_English',Georgia,serif] font-normal text-[56px] sm:text-7xl lg:text-8xl leading-[.95] text-[#2a1d12]">
        {t('tribunal.home.title')}
      </h1>
      <p className="mt-4 text-base sm:text-lg leading-relaxed text-[#4a3a28]">{t('tribunal.home.lede')}</p>
    </>
  );

  return (
    <section className="relative bg-[#f6efe0]">
      {/* ── Mobile : la scène en haut, le bandeau, puis le texte ── */}
      <div className="lg:hidden">
        <div className="relative h-[250px] overflow-hidden">
          {scene(true)}
          <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-b from-transparent to-[#f6efe0]" />
        </div>
        <div className="relative -mt-7 mx-4 min-h-[60px] px-3 py-2.5 rounded-2xl bg-[#fffdf8] border border-[#d9ccb2] shadow-[0_10px_24px_rgba(42,29,18,.16)] flex items-center">
          {lowerThird(40)}
        </div>
        <div className="flex justify-end px-4 mt-2.5">{courtChip}</div>
        <div className="px-4 pt-5">
          {intro}
          <div className="mt-5">{ctas}</div>
        </div>
      </div>

      {/* ── Grand écran : la salle en fond, le texte à gauche ── */}
      <div className="hidden lg:block relative h-[640px] overflow-hidden">
        {scene(false)}
        <div className="absolute inset-0 pointer-events-none bg-[linear-gradient(90deg,rgba(246,239,224,.97)_0,rgba(246,239,224,.9)_33%,rgba(246,239,224,0)_55%)]" />
        <div className="absolute left-0 top-0 h-full w-full max-w-7xl mx-auto inset-x-0 pointer-events-none">
          <div className="absolute left-8 xl:left-14 top-16 w-[540px] pointer-events-auto">
            {intro}
            <div className="mt-7">{ctas}</div>
          </div>
        </div>
        <div className="absolute right-6 top-5">{courtChip}</div>
        <div className={`absolute right-6 ${live.length ? 'bottom-[84px]' : 'bottom-6'} w-[360px] px-4 py-3 rounded-2xl bg-[#fffdf8]/95 border border-[#d9ccb2] shadow-[0_12px_30px_rgba(42,29,18,.3)]`}>
          {lowerThird(44)}
        </div>
      </div>

      {/* ── Audiences en direct ── */}
      {live.length > 0 && (
        <div className="lg:absolute lg:inset-x-0 lg:bottom-0 mt-6 lg:mt-0 lg:bg-[#2a1d12]/95 lg:text-white">
          <div className="lg:hidden flex items-center gap-2 px-4 pb-2 text-[11px] font-bold tracking-[.12em] uppercase text-[#9b3a2e]">
            <span className="w-2 h-2 rounded-full bg-[#b5523b] animate-pulse" />{t('tribunal.home.live')}
          </div>
          <div className="flex lg:grid lg:grid-cols-[150px_repeat(3,minmax(0,1fr))] gap-2.5 lg:gap-0 overflow-x-auto px-4 lg:px-0 pb-1 lg:pb-0 [scrollbar-width:none] snap-x">
            <div className="hidden lg:flex items-center gap-2 px-5 text-xs font-bold tracking-[.1em] text-[#e0a48f] uppercase">
              <span className="w-2 h-2 rounded-full bg-[#b5523b] animate-pulse" />{t('tribunal.home.live')}
            </div>
            {live.map(l => (
              <Link
                key={l.id}
                to={`/tribunal/${l.id}`}
                className="snap-start shrink-0 w-[250px] lg:w-auto min-w-0 flex lg:items-center gap-3 px-3.5 lg:px-4 py-3 lg:py-2.5 rounded-2xl lg:rounded-none bg-[#2a1d12] lg:bg-transparent lg:border-l lg:border-white/15 text-white hover:bg-[#4a3a28]"
              >
                <span className="hidden lg:block w-2 h-2 rounded-full bg-[#b5523b] shrink-0" />
                <span className="min-w-0 flex-1">
                  <span className="block font-bold text-sm truncate">{l.title}</span>
                  <span className="block text-xs text-[#d6c8ad] truncate">{t(`tribunal.phases_long.${l.current_phase}`, l.current_phase)}</span>
                </span>
                <span className="self-end lg:self-center shrink-0 text-xs font-bold text-[#d9a441]">{t('tribunal.home.enter')} →</span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
